import angular from "angular";

import { Ajax, Arrays,Invoices, Dates, Translations, CompanyAccount, PaymentChannel, SystemRole, Invoice, InvoiceType, InvoiceStatus, ODataQueryOptions, InvoiceListItem, BicHelper, Numeric, InvoicesLogic, Token, Configs } from "@salaxy/core";
import { Barcodes, QrCodes } from "@salaxy/reports";

import { InvoicesService, SessionService, UiHelpers } from "../../services";
import { InvoiceCrudController } from "./InvoiceCrudController";
import { TabButtonsController } from "../../controllers/helpers/TabButtonsController";
type PaymentChannelConfig = {
  /** Optional function that returns an action for handling multiple selected invoices. */
  multiSelectAction?: () => {text: string, onClick: () => void}
  /** Optional function that returns an array of actions for handling individual invoice items. */
  rowActions?: (item: InvoiceListItem) => {text: string, onClick: (invoice: InvoiceListItem) => Promise<void>}[]
  groupingFunction: (items: InvoiceListItem[]) => GroupedItem[]
  rowGroupActions?: (group: GroupedItem) => {text: string, onClick: (group: GroupedItem) => Promise<void>}[]
  hasExternalSystemMessagesEnabled: boolean
}

type GroupedItem = {
  id: string;
  businessObjectId?: string;
  groupType: string;
  title: string;
  sortableDate: string;
  invoices: InvoiceListItem[];
}
/**
 * Extends the InvoiceCrudController to provide
 * SEPA payment management functionality.
 */
export class InvoicePaymentListController extends InvoiceCrudController {

  /**
   * For NG-dependency injection
   */
  public static $inject = [
    "Invoices",
    "UiHelpers",
    "$location",
    "$routeParams",
    "InvoicesService",
    "AjaxNg1",
    "SessionService",
    "$http",
    "$timeout",
    "$sce",
    "$scope"
  ];

  /** URL for the OData list service, null for preventing automatic initial load */
  public get odataServiceUrl(): string {
    return null;
  }
  public groupedItems: GroupedItem[] = [];

  /** Year for invoices query */
  public queryYear = Dates.getToday();

  public selectedInvoices: any[] = [];

  /** Status filters for invoices */
  public invoiceEntityTypes = [
    { label: Translations.get("SALAXY.NG1.InvoicesComponent.all"), value: "all" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.net.label"), value: "net" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.pension.label"), value: "pension" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.tax.label"), value: "tax" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.unemployment.label"), value: "unemployment" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.union.label"), value: "union" },
    { label: Translations.get("SALAXY.ENUM.InvoiceType.payroll.label"), value: "payroll" },
  ];

  /** Invoice entity type used in filtering */
  public invoiceEntityType = this.invoiceEntityTypes.find((x) => x.value === "all");

  /** Status filters for invoices */
  public statusFilters = [
    {
      value: "all",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusAll"),
      ui: "",
    },
    {
      value: "read",
      text: "Käsittelyssä olevat",
      ui: "status eq 'read'",
    },
    {
      value: "unread",
      text: "Uudet maksut",
      ui: "status eq 'unread'",
    },
    {
      value: "paid",
      text: "Maksetut",
      ui: "status eq 'paid'",
    },
    {
      value: "canceled",
      text: "Perutut",
      ui: "status eq 'canceled'",
    },
    {
      value: "forecast",
      text: Translations.get("SALAXY.NG1.InvoicesComponent.statusForecast"),
      ui: "status eq 'forecast' or status eq 'preview'",
    },
  ];

  /** The current status filter. "waitingConfirmation" is the default. */
  public statusFilter = this.statusFilters.find((x) => x.value === "all");

  /** Default sepa channel */
  public paymentChannel: PaymentChannel;

  public currentTab: "netpaymentsandforeclosure" | "monthlypayments" = "netpaymentsandforeclosure";

  public paymentChannelConfig: PaymentChannelConfig = null;

  public companyAccount: CompanyAccount;

  public sortMode: "asc" | "desc" = "desc";

  /** Filter for other party name */
  public otherPartyNameFilter = "";

  /** Filter for reference number */
  public referenceFilter = "";

  /** Filter for IBAN number */
  public ibanFilter = "";

  private qrCodesCache: { [key: string]: string } = {};

  private rowActionsCache: { [key: string]: any[] } = {};
  private rowGroupActionsCache: { [key: string]: any[] } = {};
  /** Tab buttons component controller, to programmatically change tabs. */
  private tabButtonsCtrl: TabButtonsController;
  constructor(
    invoicesFullApi: Invoices,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private invService: InvoicesService,
    private sepaAjax: Ajax,
    private session: SessionService,
    private $http: angular.IHttpService,
    private $timeout: angular.ITimeoutService,
    private $sce: angular.ISCEService,
    private $scope: angular.IScope
  ) {
    // Dependency injection
    super(invoicesFullApi, uiHelpers, $location, $routeParams, invService, sepaAjax);
  }

  /** Intitializes the controller */
  public $onInit() {
    this.paymentChannel ??= this.invService.defaultPaymentListChannel;
    super.$onInit();
    this.$scope.$on("tabButtonsControllerReady", (event, data: { ctrl: TabButtonsController, tabsId: string }) => {
      this.tabButtonsCtrl = data.ctrl;
    });
    this.companyAccount = this.session.getCompanyAccount();
    this.loadData();
  }

  /**
   * Selects and shows a tab in the payroll details view
   */
  public selectTab(tab: "netpaymentsandforeclosure" | "monthlypayments"): void {
    this.tabButtonsCtrl.showTab(tab);
  }

  /**
   * Loads the data for the controller.
   */
  public loadData(showLoading = true, idsToUpdate: string[] | null = null) {
    let loading: any;
    if(showLoading) {
      loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    }
    this.loadAllInvoices(idsToUpdate).then((items) => {
      this.setPaymentChannelConfig();
      this.updategroupedItems(items, !!idsToUpdate);
      if(showLoading) {
        loading.dismiss();
      }
      this.$timeout();
    });
  }

  /**
   * Checks if multi-select functionality is supported by the current payment channel configuration
   * @returns True if multi-select is supported, false otherwise
   */
  public supportItemMultiSelect(): boolean {
    switch(this.paymentChannel) {
      case PaymentChannel.PalkkausWS:
        return this.isStronglyAuthorizedPayer() && !!this.paymentChannelConfig?.multiSelectAction;
      default:
        return !!this.paymentChannelConfig?.multiSelectAction;
    }
  }

  /**
   * Checks if the current payment channel configuration supports system messages
   * @returns True if system messages are supported, false otherwise
   */
  public hasExternalSystemMessagesEnabled(): boolean {
    return !!this.paymentChannelConfig?.hasExternalSystemMessagesEnabled;
  }

  /**
   * Checks if a given invoice item is currently selected
   * @param item The invoice item to check
   * @returns True if the item is in the selectedInvoices array, false otherwise
   */
  public isSelected(item: any) {
    return this.selectedInvoices.findIndex((x) => x.id === item.id) >= 0;
  }

  /**
   * Sets the selected state of an invoice item
   * @param item The invoice item to select/unselect
   * @param value True to select, false to unselect, "toggle" to toggle selection state
   */
  public setSelected(item: any, value: true | false | "toggle" = true) {
    const ix = this.selectedInvoices.findIndex((x) => x.id === item.id);
    const exists = ix >= 0;
    if (exists) {
      this.selectedInvoices.splice(ix, 1);
    }
    switch (value) {
      case true:
        this.selectedInvoices.push(item);
        break;
      case false:
        // Already done
        break;
      case "toggle":
        if (!exists) {
          this.selectedInvoices.push(item);
        }
        break;
    }
  }

  /**
   * Determines if a given invoice item is selectable based on the current payment channel.
   * For the PalkkausWS payment channel, only invoices with status WaitingConfirmation are selectable.
   * For other payment channels, no items are selectable.
   * @param {InvoiceListItem} item - The invoice item to check for selectability.
   * @returns {boolean} True if the item is selectable, false otherwise.
   */
  public isItemSelectable(item: InvoiceListItem): boolean {
    switch(this.paymentChannel) {
      case PaymentChannel.PalkkausWS:
        return item.status === InvoiceStatus.WaitingConfirmation;
      default:
        return false;
    }
  }

  /**
   * Sets the payment channel configuration based on the current payment channel.
   * This method calls getPaymentChannelConfig() to get the appropriate configuration
   * and assigns it to the paymentChannelConfig property.
   */
  public setPaymentChannelConfig() {
    this.paymentChannelConfig = this.getPaymentChannelConfig();
  }

  /**
   * Checks if the current user is an authorized payer.
   * This method is only used when the payment channel is PalkkausWS.
   * @returns True if the user is strongly authenticated (authorized payer), false otherwise.
   */
  public isStronglyAuthorizedPayer(): boolean {
    return !!Token.parsePayload(this.sepaAjax.getCurrentToken()).act;
  }

  /**
   * Opens a strong authentication dialog for the user.
   * This method launches an external dialog for strong authentication using a third-party service.
   * If the authentication is successful and a token is received, it exchanges the token for a new access token
   * via the backend OAuth2 token exchange endpoint. The new access token is then set as the current token.
   * If authentication fails or no token is received, an error is shown to the user.
   * @returns {Promise<void>} A promise that resolves when the authentication flow is complete.
   * @throws {Error} If no token is received from the authentication dialog or the token exchange fails.
   */
  public async openStrongAuthDialog(): Promise<void> {
    let loader: any;
    try {
      const url = Configs.current.isTestData ? "https://rc-iam.palkkaus.fi/strong-auth/index.html" : "https://iam.palkkaus.fi/strong-auth/index.html";
      const result = await this.uiHelpers.showExternalDialog("strong-auth", url, null, null, null);
      loader = this.uiHelpers.showLoading("Vahva tunnistus", "Avataan vahva tunnistus...");
      if (!result?.event?.data?.token) {
        throw new Error("No token received from strong authentication");
      }

      const response = await this.sepaAjax.postJSON(`${this.sepaAjax.getServerAddress()}/oauth2/token`, {
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        subject_token: this.sepaAjax.getCurrentToken(),
        actor_token: result.event.data.token
      });
      if (!response?.access_token) {
        throw new Error("No access token received from token exchange");
      }
      this.sepaAjax.setCurrentToken(response.access_token);
      this.$timeout();
    } catch (error) {
      console.error("Strong authentication failed:", error);
      this.uiHelpers.showAlert("Virhe", "Vahva tunnistus epäonnistui. Yritä uudelleen.");
      throw error;
    } finally {
      loader.dismiss();
    }
  }

  /**
   * Gets the payment channel configuration based on the current payment channel.
   * @returns A configuration object containing row actions for invoice items.
   * Currently only handles PaymentChannel.PalkkausManual channel.
   * For PalkkausManual channel, returns an object with:
   * - rowActions: Function that returns an array of actions available for an invoice item
   * - For unread/read status: Adds "Merkitse maksetuiksi" (Mark as paid) action
   * - For unread/read/paid status: Adds "Peru lasku" (Cancel invoice) action
   * - Actions are cached by invoice ID and status
   * Each action has:
   * - text: Display text for the action
   * - onClick: Handler function that shows confirmation dialog and updates invoice status
   */
  public getPaymentChannelConfig(): PaymentChannelConfig | null {
    const changeInvoiceStatus = (invoiceIds: string[], status: InvoiceStatus): Promise<void> => {
      const confirmHeading = status === InvoiceStatus.Canceled
        ? "Haluatko varmasti perua valitut laskut?"
        : "Haluatko varmasti siirtää valitut laskut maksuun?";
      const loaderHeading = status === InvoiceStatus.Canceled
        ? "Perutaan laskuja... Tämä voi kestää hetken riippuen laskujen määrästä."
        : "Siirretään laskuja maksuun... Tämä voi kestää hetken riippuen laskujen määrästä.";
      return this.uiHelpers.showConfirm("Vahvistus",confirmHeading).then((result) => {
        if(result) {
          const loader = this.uiHelpers.showLoading(loaderHeading);
          return Promise.all(invoiceIds.map((id, index) => {
            loader.setText(`${index}/${invoiceIds.length}`);
            return (this.api as Invoices).requestStatusUpdate(id, status);
          })).then(() => {
            return this.uiHelpers.wait().then(() => {
              this.selectedInvoices = [];
              this.loadData(false, invoiceIds);
              this.setPaymentChannelConfig();
              loader.dismiss();
            });
          });
        }
      });
    };
    switch(this.paymentChannel) {
      case PaymentChannel.Hausvise:
        return {
          hasExternalSystemMessagesEnabled: false,
          rowActions: (item: InvoiceListItem): any[] => {
            const cacheKey = `${item.id}-${item.status}`;
            if (this.rowActionsCache[cacheKey]) {
              return this.rowActionsCache[cacheKey];
            }

            const actions: {text: string, onClick: (invoice: InvoiceListItem) => Promise<void>}[] = [];
            const markAsPaidStatuses = [InvoiceStatus.Unread, InvoiceStatus.Read];
            const markAsCancelledStatuses = [InvoiceStatus.Unread, InvoiceStatus.Read, InvoiceStatus.Paid];
            const invoiceMarkAsPaidAllowed = (invoice: InvoiceListItem): boolean => {
              if(invoice.entityType === InvoiceType.Payroll) {
                return false;
              }
              return true;
            };
            const invoiceCancellingAllowed = (invoice: InvoiceListItem): boolean => {
              if(invoice.entityType === InvoiceType.Payroll) {
                return false;
              }
              return true;
            };
            const updateStatus = (invoice: InvoiceListItem, newStatus: InvoiceStatus, confirmMessage: string) => {
              return this.uiHelpers.showConfirm("HUOM", confirmMessage).then((result) => {
                if(result) {
                  const loading = this.uiHelpers.showLoading("Päivitetään laskun tilaa...");
                  return (this.api as Invoices).requestStatusUpdate(invoice.id, newStatus).then(() => {
                    return this.uiHelpers.wait().then(() => {
                      this.loadData(false);
                      loading.dismiss();
                    });
                  });
                }
              });
            };

            if(invoiceMarkAsPaidAllowed(item) && markAsPaidStatuses.includes(item.status)) {
              actions.push({
                text: "Merkitse maksetuiksi",
                onClick: (invoice: InvoiceListItem) => {
                  return updateStatus(invoice, InvoiceStatus.Paid, "Merkitse maksetuksi vain, jos olet maksanut laskun muulla tavalla kuin Hausvisessa");
                }
              });
            }

            if(invoiceCancellingAllowed(item) && markAsCancelledStatuses.includes(item.status)) {
              actions.push({
                text: "Peru lasku",
                onClick: (invoice: InvoiceListItem) => {
                  return updateStatus(invoice, InvoiceStatus.Canceled, "Peru lasku vain, jos Hausvise ei poikkeuksellisesti pysty käsittelemään laskua");
                }
              });
            }

            this.rowActionsCache[cacheKey] = actions;
            return actions;
          },
          groupingFunction: (items: InvoiceListItem[]) => {
            const result: GroupedItem[] = [];
            items.forEach((item) => {
              if(item.entityType === InvoiceType.Net || item.entityType === InvoiceType.Foreclosure || item.entityType === InvoiceType.Payroll) {
                if(item.data.payrollId) {
                  const payrollId = item.data.payrollId;
                  let group = result.find((x) => x.id === payrollId);
                  if(!group) {
                    group = {
                      id: payrollId,
                      businessObjectId: payrollId,
                      groupType: "payroll",
                      title: item.data.payrollTitle,
                      sortableDate: item.data.dueDate,
                      invoices: [],
                    };
                    result.push(group);
                  }
                  group.invoices.push(item);
                } else {
                  let group = result.find((x) => x.id === item.businessObjects[0]);
                  if(!group) {
                    group = {
                      id: item.businessObjects[0],
                      businessObjectId: item.businessObjects[0],
                      groupType: "calculation",
                      title: "Palkkalaskelma",
                      sortableDate: item.data.dueDate,
                      invoices: []
                    };
                    result.push(group);
                  }
                  group.invoices.push(item);
                }
              }
              if(item.entityType === InvoiceType.Tax || item.entityType === InvoiceType.Union || item.entityType === InvoiceType.Unemployment || item.entityType === InvoiceType.Pension) {
                const id = Dates.getYear(item.logicalDate) + "-" + Dates.getMonth(item.logicalDate);
                let group = result.find((x) => x.id === id);
                if(!group) {
                  group = {
                    id: id,
                    groupType: "monthly",
                    title: `${Translations.get(`SALAXY.ENUM.CalendarMonth.month${Dates.getMonth(item.logicalDate)}.label`)} ${Dates.getYear(item.logicalDate)}`,
                    sortableDate: item.data.dueDate,
                    invoices: [],
                  };
                  result.push(group);
                }
                group.invoices.push(item);
              }
            });
            result.filter(group => group.groupType === "monthly").forEach(group => {
              group.invoices.sort((a, b) => a.data.dueDate.localeCompare(b.data.dueDate));
            });
            return result;
          }
        };
      case PaymentChannel.PalkkausPSD: {
        const handlePsdPayment = (invoiceIds: string) => {
          const logic = {
            banks: [
              {
                id: "OSUUSPANKKI_MOCK",
                name: "Osuuspankki Mock (hyväksyy maksut automaattisesti)",
                logo: "https://cdn.salaxy.com/img/salaxy/sign/op.png"
              },   {
                id: "OSUUSPANKKI_TEST",
                name: "Osuuspankki Test (tunnistautuminen)",
                logo: "https://cdn.salaxy.com/img/salaxy/sign/op.png"
              },
              {
                id: "NORDEA_MOCK",
                name: "Nordea Test Mock (hyväksyy maksut automaattisesti)",
                logo: "https://cdn.salaxy.com/img/salaxy/sign/nordea.png"
              },
              {
                id: "NORDEA_TEST",
                name: "Nordea Test (tunnistautuminen ei toimi, jää kesken)",
                logo: "https://cdn.salaxy.com/img/salaxy/sign/nordea.png"
              },
              {
                id: "NORDEA_PERSONAL_TEST",
                name: "Nordea Personal Test (Hylkää maksut pankin puolelta)",
                logo: "https://cdn.salaxy.com/img/salaxy/sign/nordea.png"
              },
              {
                id: "INVALID_BANK_TEST",
                name: "Invalid Bank (Tekninen virhe tapahtuu ja ruudulle tulee virhe)",
                logo: "https://cdn.salaxy.com/img/salaxy/sign/invalid.png"
              }
            ],
            selectedBank: null,
            selectBank: (bank: string) => {
              logic.selectedBank = bank;
            }
          };
          this.uiHelpers.openEditDialog("salaxy-components/modals/payment/psd/BankSelection.html", null, logic).then((result) => {
            if(result.action === "ok") {
              const bank = result.logic.selectedBank;
              const url = `${this.sepaAjax.getServerAddress()}/psdPayment/payInvoices?ids=${invoiceIds}&bank=${bank}&successurl=&access_token=${this.sepaAjax.getCurrentToken()}`;
              const externalDialogLoader = this.uiHelpers.showLoading("Avataan pankkimaksua...");
              const hideExternalDialogLoader = () => {
                externalDialogLoader.dismiss();
              };
              this.uiHelpers.showExternalDialog("psdDialog", url, null, null, null, hideExternalDialogLoader).then((result) => {
                const loader = this.uiHelpers.showLoading();
                if (result.action == "user-cancel") {
                  this.uiHelpers.showDialog("salaxy-components/modals/payment/PaymentCancel.html").then(() => {
                    this.uiHelpers.wait().then(() => {
                      this.loadData(false);
                      loader.dismiss();
                    });
                  });
                } else if (result.action == "ok") {
                  this.uiHelpers.showDialog("salaxy-components/modals/payment/PaymentConfirm.html").then(() => {
                    this.uiHelpers.wait().then(() => {
                      this.loadData(false);
                      loader.dismiss();
                    });
                  });
                } else if (result.action == "error") {
                  this.uiHelpers.showDialog("salaxy-components/modals/payment/PaymentError.html").then(() => {
                    this.uiHelpers.wait().then(() => {
                      this.loadData(false);
                      loader.dismiss();
                    });
                  });
                }
              });
            }
          });
        };
        return {
          hasExternalSystemMessagesEnabled: true,
          rowGroupActions: (group: GroupedItem): any[] => {
            // Sort invoices by payment amount and store statuses
            group.invoices.sort((a, b) => (b.payment ?? 0) - (a.payment ?? 0));
            const cacheKey = `${group.id}-${group.businessObjectId}-${this.paymentChannel}-${group.invoices.map(i => i.status).join()}`;
            if (this.rowGroupActionsCache[cacheKey]) {
              return this.rowGroupActionsCache[cacheKey];
            }

            const actions: {text: string, onClick: (group: GroupedItem) => void}[] = [];
            const hasPaidOrCanceledOrRead = group.invoices.some(invoice => invoice.status === InvoiceStatus.Paid || invoice.status === InvoiceStatus.Canceled || invoice.status === InvoiceStatus.Read);
            if (!hasPaidOrCanceledOrRead) {
              actions.push({
                text: "Maksa laskuryhmä",
                onClick: (group: GroupedItem) => {
                  return handlePsdPayment(group.invoices.map((x) => x.id).join(","));
                }
              });
              actions.push({
                text: "Peru laskuryhmä",
                onClick: (group: GroupedItem) => {
                  return changeInvoiceStatus(group.invoices.map((x) => x.id), InvoiceStatus.Canceled);
                }
              });
            }
            this.rowGroupActionsCache[cacheKey] = actions;
            return actions;
          },
          groupingFunction: (items: InvoiceListItem[]) => {
            const result: GroupedItem[] = [];
            items.forEach((item) => {
              if(item.entityType === InvoiceType.Net || item.entityType === InvoiceType.Foreclosure || item.entityType === InvoiceType.Union || item.entityType === InvoiceType.Tax || item.entityType === InvoiceType.Payroll) {
                if(item.data.payrollId) {
                  const payrollId = item.data.payrollId;
                  let group = result.find((x) => x.id === payrollId);
                  if(!group) {
                    group = {
                      id: payrollId,
                      businessObjectId: payrollId,
                      groupType: "payroll",
                      title: item.data.payrollTitle,
                      sortableDate: item.data.dueDate,
                      invoices: [],
                    };
                    result.push(group);
                  }
                  group.invoices.push(item);
                } else {
                  let group = result.find((x) => x.id === item.businessObjects[0]);
                  if(!group) {
                    group = {
                      id: item.businessObjects[0],
                      businessObjectId: item.businessObjects[0],
                      groupType: "calculation",
                      title: "Palkkalaskelma",
                      sortableDate: item.data.dueDate,
                      invoices: []
                    };
                    result.push(group);
                  }
                  group.invoices.push(item);
                }
              }
              if(item.entityType === InvoiceType.Unemployment || item.entityType === InvoiceType.Pension) {
                const id = Dates.getYear(item.logicalDate) + "-" + Dates.getMonth(item.logicalDate);
                let group = result.find((x) => x.id === id);
                if(!group) {
                  group = {
                    id: id,
                    groupType: "monthly",
                    title: `${Translations.get(`SALAXY.ENUM.CalendarMonth.month${Dates.getMonth(item.logicalDate)}.label`)} ${Dates.getYear(item.logicalDate)}`,
                    sortableDate: item.data.dueDate,
                    invoices: [],
                  };
                  result.push(group);
                }
                group.invoices.push(item);
              }
            });
            result.filter(group => group.groupType === "monthly").forEach(group => {
              group.invoices.sort((a, b) => a.data.dueDate.localeCompare(b.data.dueDate));
            });
            return result;
          }
        };
      }
      case PaymentChannel.PalkkausWS: {
        return {
          hasExternalSystemMessagesEnabled: true,
          multiSelectAction: () => {
            return {
              text: "Siirrä valitut laskut maksuun",
              onClick: () => {
                return changeInvoiceStatus(this.selectedInvoices.map((x) => x.id), InvoiceStatus.Unread);
              }
            };
          },
          rowActions: (item: InvoiceListItem): any[] => {
            const cacheKey = `${item.id}-${item.status}-${this.paymentChannel}-${this.isStronglyAuthorizedPayer()}`;
            if (this.rowActionsCache[cacheKey]) {
              return this.rowActionsCache[cacheKey];
            }

            const actions: {text: string, onClick: (invoice: InvoiceListItem) => void}[] = [];
            if(this.isStronglyAuthorizedPayer()) {
              if(item.status === InvoiceStatus.WaitingConfirmation) {
                actions.push({
                  text: "Siirrä maksuun",
                  onClick: (invoice: InvoiceListItem) => {
                    return changeInvoiceStatus([invoice.id], InvoiceStatus.Unread);
                  }
                });
              }
              if(item.status !== InvoiceStatus.Canceled) {
                actions.push({
                  text: "Peru lasku",
                  onClick: (invoice: InvoiceListItem) => {
                    return changeInvoiceStatus([invoice.id], InvoiceStatus.Canceled);
                  }
                });
              }
              if(item.status === InvoiceStatus.Read) {
                actions.push({
                  text: "Merkitse maksetuksi",
                  onClick: (invoice: InvoiceListItem) => {
                    return changeInvoiceStatus([invoice.id], InvoiceStatus.Paid);
                  }
                });
              }
            }
            this.rowActionsCache[cacheKey] = actions;
            return actions;
          },
          groupingFunction: (items: InvoiceListItem[]) => {
            const result: GroupedItem[] = [];
            items.forEach((item) => {
              if(item.entityType === InvoiceType.Net || item.entityType === InvoiceType.Foreclosure) {
                if(item.data.payrollId) {
                  const payrollId = item.data.payrollId;
                  let group = result.find((x) => x.id === payrollId);
                  if(!group) {
                    group = {
                      id: payrollId,
                      businessObjectId: payrollId,
                      groupType: "payroll",
                      title: item.data.payrollTitle,
                      sortableDate: item.data.dueDate,
                      invoices: [],
                    };
                    result.push(group);
                  }
                  group.invoices.push(item);
                } else {
                  result.push(
                    {
                      id: item.id,
                      businessObjectId: item.businessObjects[0],
                      groupType: "calculation",
                      title: "Palkkalaskelma",
                      sortableDate: item.data.dueDate,
                      invoices: [item],
                    }
                  );
                }
              }
              if(item.entityType === InvoiceType.Tax || item.entityType === InvoiceType.Union || item.entityType === InvoiceType.Unemployment || item.entityType === InvoiceType.Pension) {
                const id = Dates.getYear(item.logicalDate) + "-" + Dates.getMonth(item.logicalDate);
                let group = result.find((x) => x.id === id);
                if(!group) {
                  group = {
                    id: id,
                    groupType: "monthly",
                    title: `${Translations.get(`SALAXY.ENUM.CalendarMonth.month${Dates.getMonth(item.logicalDate)}.label`)} ${Dates.getYear(item.logicalDate)}`,
                    sortableDate: item.data.dueDate,
                    invoices: [],
                  };
                  result.push(group);
                }
                group.invoices.push(item);
              }
            });
            result.filter(group => group.groupType === "monthly").forEach(group => {
              group.invoices.sort((a, b) => a.data.dueDate.localeCompare(b.data.dueDate));
            });
            return result;
          }
        };
      }
        default:
        return null;
    }
  }

  /**
   * Returns the CSS class for a given VismaPay status.
   * These statuses are from VismaPay.
   * @param status The VismaPay status string.
   * @returns The corresponding badge CSS class.
   * TODO: More later to config if more statuses are needed.
   */
  public getBadgeClass(status: string) {
    status = (status ?? "").split("\r\n")[0];
    switch(status) {
      case "SCA_REQUIRED":
      case "UNAVAILABLE":
      case "FAILED":
      case "CANCEL_FAILED":
      case "CANCELLED":
        return "badge bg-danger";
      case "SUCCEEDED":
        return "badge bg-success";
      case "PENDING":
      case "WAITING_FOR_PROCESSING":
      case "FORWARDED":
      case "CANCEL_PENDING":
        return "badge bg-warning";
      default:
        return "badge bg-info";
    }
  }

  /**
   * Gets the grouped invoice items filtered and sorted based on the current tab.
   * @returns An array of grouped invoice items, filtered and sorted by date.
   * For the "netpaymentsandforeclosure" tab, returns only net payments and foreclosures.
   * For the "monthlypayments" tab, returns only monthly payments like tax, union etc.
   * Returns empty array if no grouped items exist or for unknown tabs.
   */
  public getGroupedItems() {
    if (!this.groupedItems) {
      return [];
    }

    const sortByDate = (a: any, b: any) => {
      const sortMultiplier = this.sortMode === "asc" ? 1 : -1;
      return sortMultiplier * a.sortableDate.localeCompare(b.sortableDate);
    };
    switch (this.currentTab) {
      case "netpaymentsandforeclosure":
        return this.groupedItems
          .filter(x => x.groupType === "payroll" || x.groupType === "calculation")
          .sort(sortByDate);

      case "monthlypayments":
        return this.groupedItems
          .filter(x => x.groupType === "monthly")
          .sort(sortByDate);

      default:
        return [];
    }
  }

  /**
   * Toggles the sort mode between ascending and descending.
   * This affects the order of grouped items when displayed.
   */
  public changeSortMode() {
    this.sortMode = this.sortMode === "asc" ? "desc" : "asc";
  }

  /**
   * Updates the grouped items based on the provided invoice list items.
   * Groups net payments and foreclosures by payroll or calculation.
   * Groups monthly payments (tax, union, unemployment, pension) by month.
   * @param items List of invoice items to group
   */
  public updategroupedItems(items: InvoiceListItem[], updateSpecificInvoices: boolean = false) {
    if(updateSpecificInvoices) {
      // Efficiently update only the invoices that are present in the updated items list
      const updatedInvoicesMap = new Map(items.map(item => [item.id, item]));
      this.groupedItems.forEach(group => {
        group.invoices = group.invoices.map(invoice => updatedInvoicesMap.get(invoice.id) || invoice);
      });
    } else {
      this.groupedItems = [];
      this.groupedItems = this.paymentChannelConfig?.groupingFunction(items) ?? [];
    }
  }

  /**
   * Loads all invoices from the server.
   * @returns Promise of all invoices
   */
  public async loadAllInvoices(idsToUpdate: string[] | null = null): Promise<InvoiceListItem[]> {
    let items: any[] = [];
    const loadItems = (nextPageLink: string = null): Promise<string> => {
      let options: ODataQueryOptions = {$filter: `data/channel eq '${this.paymentChannel}' and endAt ge ${Dates.getYear(this.queryYear)}-01-01 and endAt le ${Dates.getYear(this.queryYear)}-12-31`, $orderby: "id"};
      const queryString = nextPageLink?.indexOf("?") ?? -1;
      if (queryString >= 0) {
        const searchParams = new URLSearchParams(nextPageLink.substr(queryString));
        if (searchParams.get("$skip")) {
          options = { $skip:  Number(searchParams.get("$skip")), $filter: `data/channel eq '${this.paymentChannel}' and endAt ge ${Dates.getYear(this.queryYear)}-01-01 and endAt le ${Dates.getYear(this.queryYear)}-12-31`, $orderby: "id"};
        }
      }
      return this.invoicesApi.getOData(options).then((data) => {
        items.push(...data.items);
        return data.nextPageLink ? loadItems(data.nextPageLink) : null;
      });
    };

    return loadItems().then(() => {
      if(idsToUpdate) {
        items = items.filter(item => idsToUpdate.includes(item.id));
      }
      return Arrays.distinct(items, (item) => item.id).sort((a, b) => a.endAt > b.endAt ? -1 : 1);
    });
  }

  /** Filter function for angularjs filter used in template */
  public filterInvoices = (invoice: InvoiceListItem) => {
    // Filter by invoice entity type
    if (this.invoiceEntityType.value !== "all" && invoice.entityType !== this.invoiceEntityType.value) {
      return false;
    }

    // Filter by status
    if (this.statusFilter.value !== "all" && invoice.status !== this.statusFilter.value) {
      return false;
    }

    // Filter by other party name
    if (this.otherPartyNameFilter &&
        !invoice.otherPartyInfo?.avatar?.displayName?.toLowerCase().includes(this.otherPartyNameFilter.toLowerCase())) {
      return false;
    }

    // Filter by reference
    if (this.referenceFilter &&
        !invoice.reference?.toLowerCase().includes(this.referenceFilter.toLowerCase())) {
      return false;
    }

    // Filter by IBAN
    if (this.ibanFilter &&
        !invoice.otherPartyInfo?.ibanNumber?.toLowerCase().includes(this.ibanFilter.toLowerCase())) {
      return false;
    }

    return true;
  };

  /**
   * Shows a dialog for a single invoice.
   * @param item Invoice item.
   */
  public showInvoiceDialog(item: InvoiceListItem) {
    this.uiHelpers.openEditDialog("salaxy-components/modals/invoices/invoice-payment-list-dialog.html", item, { parent: this }, "lg");
  }

  /**
   * Returns PDF url for a single invoice.
   * @param item Invoice list item.
   */
   public getPdfUrl(item: InvoiceListItem): string {
    if (!(item as any).pdfUrl) {
        (item as any).pdfUrl = (this.api as Invoices).getContentUrl(item.id, false);
    }
    return (item as any).pdfUrl;
  }

  /**
   * Retrieves the QR code for the specified invoice item.
   * If the QR code is not already cached, it generates the QR code and caches it for future use.
   * @param item - The invoice item for which to retrieve the QR code.
   * @returns The QR code string.
   */
  public getQrCode(item: InvoiceListItem): string {
    if(!this.qrCodesCache[item.id]) {
      this.qrCodesCache[item.id] = "loading";
      this.generateQrCodeSvgForInvoice(item).then((qrCode) => {
        if(!qrCode) {
          this.qrCodesCache[item.id] = "error";
          this.$timeout();
          return this.qrCodesCache[item.id];
        } else {
          this.qrCodesCache[item.id] = qrCode;
          this.$timeout();
          return this.$sce.trustAsHtml(this.qrCodesCache[item.id]);
        }
      });
    }
    if(this.qrCodesCache[item.id] === "error") {
      return this.qrCodesCache[item.id];
    }
    return this.$sce.trustAsHtml(this.qrCodesCache[item.id]);
  }

  /**
   * Calculates barcode value for the invoice item.
   * @param item Invoice list item.
   */
  public getBarcodeValue(item: InvoiceListItem): string {
    return Barcodes.getValue(
      item.otherPartyInfo.ibanNumber || "",
      (item as any).reference || "",
      Dates.asDate(item.data.dueDate),
      Numeric.round(item.payment * 100, 0));
  }

  /**
   * Retrieves the barcode for the invoice recipient's IBAN.
   * If the IBAN does not start with "fi" or is not provided, an empty string is returned.
   * The barcode is generated using the Barcodes.createBarcode function.
   * @returns The barcode as a string, wrapped in an HTML trust object.
   */
  public getBarcode(item: InvoiceListItem): string {
    if (!item.otherPartyInfo.ibanNumber || !item.otherPartyInfo.ibanNumber.toLowerCase().trim().startsWith("fi")) {
      return "";
    }
    const getDocumentAndSvgNode = () => {
      return {document, svgNode: document.createElementNS("http://www.w3.org/2000/svg", "svg")};
    };

    const result = Barcodes.createBarcode(
      getDocumentAndSvgNode,
      item.otherPartyInfo.ibanNumber || "",
      (item as any).reference || "",
      Dates.asDate(item.data.dueDate),
      Numeric.round(item.payment * 100, 0),
      {
        width: 1.8,
        height: 32,
        displayValue: true,
        fontSize: 15
      }
    );
    if(!result) {
      return "";
    }
    return this.$sce.trustAsHtml(new XMLSerializer().serializeToString(result as any));
  }

  /** Hides bar code for forecast and preview invoice statuses*/
  public hideBarCode(item: InvoiceListItem): boolean {
    if(item.status === InvoiceStatus.Forecast || item.status === InvoiceStatus.Preview) {
      return true;
    }
    return false;
  }

  /**
   * Copies the barcode to clipboard.
   * @param item Invoice list item.
   */
  public copyBarcode(item: InvoiceListItem) {
    (navigator as any).clipboard.writeText(this.getBarcodeValue(item));
  }

  /** Gets and avatar image for a payment channel */
  public getChannelAvatar(channel: PaymentChannel) {
    return InvoicesLogic.getChannelAvatar(channel);
  }

  /** Data reader for one item. */
  public getGroupItemDataReader = (item: InvoiceListItem, status: InvoiceStatus, fileTypeId = null) => {
    return {
      /** Function to read data into given array */
      read: (arr: Invoice[], message: string): Promise<void> => {
        const showConfirmation = (): Promise<boolean> => {
          const warning = this.getEprWarning([item], status);
          if (warning) {
            return this.uiHelpers.showConfirm("Huomioi palkkatietoilmoitukset!", warning);
          }
          return Promise.resolve(true);
        };
        return showConfirmation().then((result) => {
          if (result) {
            (item as any)["isReading" + (status || "")] = true;
            return this.getInvoice(item, message, status).then((data) => {
              if (data) {
                arr.push(data);
              }
              (item as any)["isReading" + (status || "")] = false;
              return;
            });
          } else {
            return;
          }
        });
      },
      getSepaData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<string> => this.getSepaData(arr, null, fileTypeId),
      getFinvoiceData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<Blob[]> => this.getFinvoiceData(arr),
      getPdfData: (
        /** Array for invoices */
        arr: Invoice[],
      ): Promise<Blob[]> => this.getPdfData(arr),
      /** Optional label for data export */
      exportLabel: null,
      /** Indicates if the export is not possible */
      disabled: () => false,
    };
  };

  private getInvoice(item: InvoiceListItem, message: any, status: InvoiceStatus = null): Promise<Invoice> {

    const updateInvoiceStatus = (invoice: Invoice, status: InvoiceStatus): Promise<Invoice> => {
      if (invoice.header.status === status) {
        return Promise.resolve(invoice);
      }
      return (this.api as Invoices).requestStatusUpdate(item.id, status);
    };

    return this.api.getSingle(item.id)
      .then((readInvoice: Invoice) => {
        if (status) {
          return updateInvoiceStatus(readInvoice, status).then((updatedInvoice) => {
            item.status = updatedInvoice.header.status;
            return updatedInvoice;
          });
        } else {
          return readInvoice;
        }
      });
  }

  private async getPdfData(invoices: Invoice[]): Promise<Blob[]> {
    const blobs: Blob[] = [];
    const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    for (const [index, invoice] of invoices.entries()) {
      const blob = await this.download("/v03/api/invoices/content", invoice, "blob");
      loader.setText(`${index}/${invoices.length}`);
      blobs.push(blob);
    }
    loader.dismiss();
    return blobs;
  }

  private async getFinvoiceData(invoices: Invoice[]): Promise<Blob[]> {
    const blobs: Blob[] = [];
    const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.loading");
    for (const [index, invoice] of invoices.entries()) {
      const blob = await this.download("/v03/api/invoices/finvoice", invoice, "blob");
      loader.setText(`${index}/${invoices.length}`);
      blobs.push(blob);
    }
    loader.dismiss();
    return blobs;
  }

  private getSepaData(invoices: Invoice[], payerIbanNumber: string = null, fileTypeId: string = null): Promise<string> {
    const version = fileTypeId === "sepaFileV03" || fileTypeId === "sepaCopyV03" ? "pain.001.001.03" : "pain.001.001.02";
    const method = `/v03/api/invoices/sepa?version=${version}${payerIbanNumber ? "&payerIbanNumber=" + encodeURIComponent(payerIbanNumber) : ""}`;
    return this.download(method, invoices, "text");
  }
  private download(method: string, data: any, responseType: string): Promise<any> {

    const request: any = {}; // angular.IRequestConfig

    const token: string = this.sepaAjax.getCurrentToken();
    if (token) {
      request.headers = { Authorization: "Bearer " + token };
    }

    request.url = this.sepaAjax.getServerAddress() + method;
    request.method = "POST";
    request.data = data;
    request.responseType = responseType;
    request.withCredentials = (token) ? false : this.sepaAjax.useCredentials;

    return (this.$http(request).then(
      (response) => response.data,
      (error: any) => {
        return null;
      }) as any
    );
  }

  private getEprWarning(items: InvoiceListItem[], status: InvoiceStatus): string {
    const isDelayed = (salaryDate?: string): boolean => {
      if (this.session.isInRole(SystemRole.Test)) {
        const officialDate = Dates.add((salaryDate ?? "today"),5, "day");
        const expiresAt = Dates.addWorkdays(officialDate, 0);
        const today = Dates.asDate("today");
        return today>expiresAt;
      }
      return false;
    };
    if (status === InvoiceStatus.Canceled) {
      for (const item of items) {
        if (item.status === InvoiceStatus.Paid &&
          item.entityType == InvoiceType.Net &&
          isDelayed(item.logicalDate)) {
          return "Palkanmaksusta tehdyt tulorekisteri-ilmoitukset perutaan automaattisesti.\n\nJos maksat palkan uudestaan samalle palkkapäivälle, ota huomioon, että uudesta palkkatietoilmoituksesta saattaa seurata myöhästymismaksu. Haluatko varmasti perua maksut?";
        }
      }
    } else if (status === InvoiceStatus.Paid) {
      for (const item of items) {
        if (item.status !== InvoiceStatus.Paid &&
          item.entityType == InvoiceType.Net &&
          isDelayed(item.logicalDate)) {
          return "Annetulle palkkapäivälle tehtävä palkkatietoilmoitus myöhästyy, koska ilmoitus pitää tehdä 5 päivän kuluessa palkkapäivästä. Ota huomioon, että tästä saattaa seurata myöhästymismaksu. Haluatko varmasti merkitä palkan maksetuiksi?";
        }
      }
    }
    return null;
  }

  private async generateQrCodeSvgForInvoice(item: InvoiceListItem): Promise<string> {
    const svg = await QrCodes.getValue(
     BicHelper.getBicForIban(item.otherPartyInfo.ibanNumber.replace(/\s+/g, "")),
     item.otherPartyInfo.avatar.displayName,
     item.otherPartyInfo?.ibanNumber,
     item.reference,
     null,
     item.payment,
     item.endAt
   );
   return svg;
 }
}
